import { useState } from 'react'
import type { FoundryAsset } from '../types'
import { TagFilter } from './TagFilter'

interface Props {
  asset: FoundryAsset
  onClose: () => void
  onUpdate: (id: string, updates: { name?: string; tags?: string[] }) => Promise<void>
  onDelete: (id: string) => Promise<void>
}

export function AssetDetail({ asset, onClose, onUpdate, onDelete }: Props) {
  const [name, setName] = useState(asset.name)
  const [tags, setTags] = useState<string[]>(asset.tags)
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const dirty = name !== asset.name || JSON.stringify(tags) !== JSON.stringify(asset.tags)

  const handleSave = async () => {
    setSaving(true)
    await onUpdate(asset.id, { name, tags })
    setSaving(false)
  }

  const handleDelete = async () => {
    await onDelete(asset.id)
    onClose()
  }

  const toggleTag = (tag: string) => {
    setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]))
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-forge-basalt border border-forge-slate rounded-xl max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-forge-slate">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${
            asset.type === 'character'
              ? 'bg-forge-ember/10 text-forge-ember'
              : 'bg-forge-gold/10 text-forge-gold'
          }`}>
            {asset.type}
          </span>
          <h2 className="text-base font-bold text-forge-primary flex-1">{asset.name}</h2>
          <button onClick={onClose} className="text-forge-ash hover:text-forge-primary transition-colors">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-5 space-y-4">
          {/* Image */}
          <div className="rounded-lg overflow-hidden border border-forge-slate bg-forge-obsidian">
            <img src={asset.url} alt={asset.name} className="w-full max-h-96 object-contain" />
          </div>

          {/* Name field */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-forge-ash font-semibold block mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-forge-obsidian border border-forge-slate rounded-md px-3 py-2 text-sm text-forge-primary focus:border-forge-ember focus:outline-none"
            />
          </div>

          {/* Prompt (read-only) */}
          {asset.prompt && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-forge-ash font-semibold block mb-1">Generation Prompt</label>
              <p className="text-[12px] text-forge-secondary bg-forge-obsidian border border-forge-slate rounded-md px-3 py-2 whitespace-pre-wrap">
                {asset.prompt}
              </p>
            </div>
          )}

          {/* Tags */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-forge-ash font-semibold block mb-1.5">Tags</label>
            <TagFilter type={asset.type} selectedTags={tags} onToggleTag={toggleTag} />
          </div>

          {/* Metadata */}
          <div className="text-[10px] text-forge-cool-smoke space-y-0.5">
            <p>Source: {asset.source} &middot; Created: {new Date(asset.created_at).toLocaleDateString()}</p>
            {'model' in asset.metadata && <p>Model: {String(asset.metadata.model)}</p>}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-5 py-3 border-t border-forge-slate">
          {confirmDelete ? (
            <>
              <span className="text-[11px] text-forge-heat-critical">Permanently delete this asset?</span>
              <div className="flex-1" />
              <button
                onClick={() => setConfirmDelete(false)}
                className="text-[11px] font-semibold rounded-md px-3 py-1.5 border border-forge-slate text-forge-ash hover:text-forge-primary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="text-[11px] font-semibold rounded-md px-3 py-1.5 bg-forge-heat-critical text-white hover:bg-forge-heat-critical/80 transition-colors"
              >
                Delete
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setConfirmDelete(true)}
                className="text-[11px] font-semibold text-forge-heat-critical/60 hover:text-forge-heat-critical transition-colors"
              >
                Delete Asset
              </button>
              <div className="flex-1" />
              <button
                onClick={onClose}
                className="text-[11px] font-semibold rounded-md px-3 py-1.5 border border-forge-slate text-forge-ash hover:text-forge-primary transition-colors"
              >
                Cancel
              </button>
              {dirty && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="text-[11px] font-semibold rounded-md px-4 py-1.5 bg-forge-ember text-white hover:bg-forge-ember/80 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
